import i18n from '@/utils/i18n';
import { ActivityIndicator } from '@/components/activity-indicator';
import { createStyles } from '@/contexts/theme-context';
import { FlatList, TouchableOpacity, View } from 'react-native';
import { ItemSubtitleV2 } from '@/components/item-subtitle-v2';
import { ListItem } from '@usepepper/ui/molecules/list-item';
import { PackUnit } from '@/enums';
import { RelatedVariantPack } from '@/hooks/items/use-variant-pack';
import { SectionEmptyState } from '@usepepper/ui/atoms/section-empty-state';

interface Props {
  loading: boolean;
  onPressVariantPack?: (uuid: string) => void;
  variantPacks: RelatedVariantPack[];
}

const VariantsPackEmptyState = () => {
  const [styles] = useStyles();

  return (
    <View style={styles.emptyStateContainer}>
      <SectionEmptyState
        text={i18n.t('item_details.no_variant_packs_found.title')}
      />
    </View>
  );
};

export const RelatedVariantPacksList = ({
  loading,
  onPressVariantPack,
  variantPacks,
}: Props) => {
  const [styles] = useStyles();

  if (loading) {
    return (
      <View
        accessibilityState={{
          busy: true,
        }}
        style={styles.loadingContainer}
      >
        <ActivityIndicator
          accessibilityLabel={i18n.t('related_variant_packs.loading')}
          size={32}
        />
      </View>
    );
  }

  return (
    <FlatList
      contentContainerStyle={styles.variantPacksList}
      data={variantPacks}
      keyExtractor={(item) => item.variant_pack.uuid}
      ListEmptyComponent={<VariantsPackEmptyState />}
      renderItem={({ item }) => (
        <TouchableOpacity
          disabled={!item.variant_pack.uuid || !onPressVariantPack}
          onPress={() => onPressVariantPack?.(item.variant_pack.uuid)}
        >
          <ListItem
            style={styles.variantPackListItem}
            title={
              <ItemSubtitleV2
                baseUnit={item.variant_pack.pack.unit as PackUnit}
                currencyCode={item.currency_code}
                displayString={
                  item.variant_pack.pack.unit
                    ? `${item.variant_pack.pack.unit.charAt(0).toUpperCase()}${item.variant_pack.pack.unit.slice(1).toLowerCase()}`
                    : ''
                }
                sku={item.variant_pack.external_item_id}
                unitCount={1}
                unitPriceInMicros={item.price_in_micros ?? 0}
              />
            }
          />
        </TouchableOpacity>
      )}
      scrollEnabled={false}
      showsVerticalScrollIndicator={false}
    />
  );
};

const useStyles = createStyles((theme) => ({
  emptyStateContainer: {
    paddingHorizontal: 64,
    paddingVertical: 30,
  },
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  variantPackListItem: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: 8,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  variantPacksList: {
    gap: 12,
  },
}));
